import React from 'react';
import type { ShoppableProduct } from './types';

interface HotspotProps {
  product: ShoppableProduct;
  isActive: boolean;
  onHover: (product: ShoppableProduct, anchor: HTMLElement) => void;
  onLeave: () => void;
  onOpen: (product: ShoppableProduct) => void;
}

/** The "+" on a garment: a white core in a translucent ring, centred on the hotspot position.
 *  The ring's white edge, the core and the "+" are ONE svg on one 48-unit grid, all centred on (24, 24),
 *  so they stay concentric at any size and any sub-pixel position (Paula, 2026-10-23: "completely
 *  centred, the same distance on all sides"). As separate boxes they were laid out concentric but
 *  rounded apart: the hotspot sits at a fractional position, the 13px "+" had 7.5px either side, and
 *  the core's downward shadow (shadow-md, 0 4px 6px) made it read low in the ring. The span behind the
 *  svg only blurs and darkens the picture, with an even shadow all round. */
const HotspotMark: React.FC = () => (
  <svg className="hotspot-mark absolute inset-0 h-full w-full" viewBox="0 0 48 48" aria-hidden="true">
    <circle cx="24" cy="24" r="23.25" fill="none" stroke="white" strokeOpacity="0.98" strokeWidth="1.5" />
    <circle cx="24" cy="24" r="14" fill="white" />
    <path d="M24 18v12M18 24h12" stroke="#030712" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const Hotspot: React.FC<HotspotProps> = ({ product, isActive, onHover, onLeave, onOpen }) => (
  <button
    type="button"
    data-silvr-ui="true"
    className={`hotspot absolute z-20 flex h-11 w-11 items-center justify-center rounded-full transition-transform duration-200 hover:scale-110 focus-visible:scale-110 sm:h-12 sm:w-12 ${isActive ? 'scale-110' : ''}`}
    style={{ top: `${product.y}%`, left: `${product.x}%`, transform: 'translate(-50%, -50%)' }}
    onMouseEnter={(event) => onHover(product, event.currentTarget)}
    onMouseLeave={onLeave}
    onFocus={(event) => onHover(product, event.currentTarget)}
    onClick={(event) => {
      event.stopPropagation();
      onOpen(product);
    }}
    aria-label={`Explore ${product.name}`}
    aria-expanded={isActive}
  >
    <span className="hotspot-ring absolute inset-0 rounded-full" />
    <HotspotMark />
  </button>
);

export default Hotspot;
